import { useEffect, useMemo, useState } from "preact/hooks"
import useSWR, { useSWRConfig } from "swr"
import { format, parseISO } from "date-fns"
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  LabelList,
  ReferenceLine,
  ResponsiveContainer,
  Scatter,
  ScatterChart,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts"
import { HelpCircle, Loader2, RefreshCw, Search } from "lucide-react"
import { Button } from "../../components/ui/button"
import { Card, CardContent, CardHeader } from "../../components/ui/card"
import { Input } from "../../components/ui/input"
import { Label } from "../../components/ui/label"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select"
import { ChartContainer } from "../../components/ui/chart"
import {
  getBrandWeeklyMovement,
  getSpinsBrandComparison,
  getSpinsBrands,
  getSpinsOverview,
  getSpinsProducts,
  listDistributorReports,
} from "../sales_forecasting/api/distributor_sales_api"
import {
  DistributorSalesReport,
  UploadResponse,
  BrandWeeklyMovementGeographyPoint,
  BrandWeeklyMovementResponse,
  BrandWeeklyMovementSkuPoint,
  BrandWeeklyMovementTrendPoint,
  SpinsBrandMetrics,
  SpinsProductMetrics,
} from "../sales_forecasting/types/distributor_sales"
import { DistributorSalesUpload } from "../sales_forecasting/components/distributor_sales_upload"
import { SalesWizPanel } from "./sales_wiz_panel"
import { SyndicatedMetricsImport } from "./syndicated_metrics_import"

function to_number(value: number | string | null | undefined): number {
  if (value === null || value === undefined) return 0
  if (typeof value === "number") return value
  const parsed = Number.parseFloat(value)
  return Number.isFinite(parsed) ? parsed : 0
}

function format_currency(value: number | string | null | undefined): string {
  const num = to_number(value)
  if (Math.abs(num) >= 1000000) return `$${(num / 1000000).toFixed(1)}M`
  if (Math.abs(num) >= 1000) return `$${(num / 1000).toFixed(0)}K`
  return `$${num.toFixed(0)}`
}

function format_number(value: number | string | null | undefined): string {
  return Math.round(to_number(value)).toLocaleString()
}

function format_percent(value: number | null | undefined): string {
  if (value === null || value === undefined || !Number.isFinite(value)) return "-"
  return `${value.toFixed(1)}%`
}

function format_date_label(value?: string | null): string {
  if (!value) return "-"
  return format(parseISO(value), "MMM d, yyyy")
}

function format_percent_value(value: number | string | null | undefined): string {
  return format_percent(to_number(value))
}

function format_sku_spp(product: SpinsProductMetrics): string {
  if (product.dollars_per_tdp === null || product.dollars_per_tdp === undefined) return "-"
  return format_currency(product.dollars_per_tdp)
}

function metric_color(value: number | null | undefined): string {
  if (value === null || value === undefined) return "text-gray-500"
  if (value > 0) return "text-emerald-700"
  if (value < 0) return "text-red-700"
  return "text-gray-700"
}

function sales_bar_fill(row: SpinsBrandMetrics, uploadBrandName?: string | null): string {
  if (uploadBrandName && row.brand === uploadBrandName) return "#f97316"
  return "#2563eb"
}

function growth_bar_fill(row: SpinsBrandMetrics, uploadBrandName?: string | null): string {
  if (uploadBrandName && row.brand === uploadBrandName) return "#fb923c"
  return (row.dollars_pct_change ?? 0) < 0 ? "#dc2626" : "#16a34a"
}

function ChartLegend() {
  return (
    <div class="mt-3 flex items-center justify-center gap-5 text-sm text-gray-700">
      <span class="inline-flex items-center gap-2">
        <span class="h-3 w-3 rounded-sm bg-blue-600" />
        Sales $
      </span>
      <span class="inline-flex items-center gap-2">
        <span class="h-3 w-3 rounded-sm bg-emerald-600" />
        Positive YoY %
      </span>
      <span class="inline-flex items-center gap-2">
        <span class="h-3 w-3 rounded-sm bg-red-600" />
        Negative YoY %
      </span>
      <span class="inline-flex items-center gap-2">
        <span class="h-3 w-3 rounded-sm bg-orange-500" />
        My Brand
      </span>
    </div>
  )
}

function ScatterLegend(props: { uploadBrandName?: string | null }) {
  return (
    <div class="mt-3 flex items-center justify-center gap-5 text-sm text-gray-700">
      <span class="inline-flex items-center gap-2">
        <span class="h-3 w-3 rounded-full bg-blue-600" />
        Comparison brands
      </span>
      <span class="inline-flex items-center gap-2">
        <span class="h-3 w-3 rounded-full bg-orange-500" />
        {props.uploadBrandName || "My Brand"}
      </span>
    </div>
  )
}

function BrandComparisonTooltip(props: any) {
  if (!props.active || !props.payload?.length) return null
  const row = props.payload[0].payload as SpinsBrandMetrics
  return (
    <div class="rounded-md border border-gray-200 bg-white p-3 text-sm shadow-sm">
      <div class="font-semibold text-gray-900">{row.brand}</div>
      <div class="mt-2 grid grid-cols-[auto_auto] gap-x-4 gap-y-1">
        <span class="text-gray-500">Sales</span>
        <span class="text-right font-medium">{format_currency(row.dollars)}</span>
        <span class="text-gray-500">Dollar change</span>
        <span class={`text-right font-medium ${metric_color(to_number(row.dollars_change))}`}>{format_currency(row.dollars_change)}</span>
        <span class="text-gray-500">Dollar growth</span>
        <span class={`text-right font-medium ${metric_color(row.dollars_pct_change)}`}>{format_percent(row.dollars_pct_change)}</span>
        <span class="text-gray-500">Share</span>
        <span class="text-right font-medium">{format_percent(row.share_pct)}</span>
        <span class="text-gray-500">TDP</span>
        <span class="text-right font-medium">{format_number(row.tdp)}</span>
      </div>
    </div>
  )
}

function GrowthDistributionTooltip(props: any) {
  if (!props.active || !props.payload?.length) return null
  const row = props.payload[0].payload
  return (
    <div class="rounded-md border border-gray-200 bg-white p-3 text-sm shadow-sm">
      <div class="font-semibold text-gray-900">{row.brand}</div>
      <div class="mt-2 grid grid-cols-[auto_auto] gap-x-4 gap-y-1">
        <span class="text-gray-500">Dollar growth</span>
        <span class={`text-right font-medium ${metric_color(row.growth)}`}>{format_percent(row.growth)}</span>
        <span class="text-gray-500">TDP change</span>
        <span class={`text-right font-medium ${metric_color(row.tdp_change)}`}>{format_number(row.tdp_change)}</span>
        <span class="text-gray-500">Sales</span>
        <span class="text-right font-medium">{format_currency(row.dollars)}</span>
      </div>
    </div>
  )
}

interface BrandMetricChartRow {
  brand: string
  value: number
  dollars: number
  avg_acv: number
  spp: number | null
  tdp: number
  is_my_brand: boolean
}

function BrandMetricTooltip(props: {
  active?: boolean;
  payload?: Array<{ payload: BrandMetricChartRow }>;
  valueFormatter: (value: number) => string;
  valueLabel: string;
}) {
  if (!props.active || !props.payload?.length) return null
  const row = props.payload[0].payload
  return (
    <div class="rounded-md border border-gray-200 bg-white p-3 text-sm shadow-sm">
      <div class="font-semibold text-gray-900">{row.brand}</div>
      <div class="mt-2 grid grid-cols-[auto_auto] gap-x-4 gap-y-1">
        <span class="text-gray-500">{props.valueLabel}</span>
        <span class="text-right font-medium">{props.valueFormatter(row.value)}</span>
        <span class="text-gray-500">Sales</span>
        <span class="text-right font-medium">{format_currency(row.dollars)}</span>
        <span class="text-gray-500">ACV %</span>
        <span class="text-right font-medium">{format_percent(row.avg_acv)}</span>
        <span class="text-gray-500">SPP</span>
        <span class="text-right font-medium">{row.spp === null ? "-" : format_currency(row.spp)}</span>
        <span class="text-gray-500">TDP</span>
        <span class="text-right font-medium">{format_number(row.tdp)}</span>
      </div>
    </div>
  )
}

function KpiCard(props: { label: string; value: string; detail?: string; tone?: string }) {
  return (
    <div class="rounded-lg border border-gray-200 bg-white p-4">
      <p class="text-xs font-medium uppercase tracking-wide text-gray-500">{props.label}</p>
      <p class={`mt-2 text-2xl font-semibold ${props.tone || "text-gray-900"}`}>{props.value}</p>
      {props.detail && <p class="mt-1 text-sm text-gray-500">{props.detail}</p>}
    </div>
  )
}

function BrandWeeklyMovementTooltip(props: any) {
  if (!props.active || !props.payload?.length) return null
  const row = props.payload[0].payload as BrandWeeklyMovementTrendPoint
  return (
    <div class="rounded-md border border-gray-200 bg-white p-3 text-sm shadow-sm">
      <div class="font-semibold text-gray-900">{format_date_label(row.end_date)}</div>
      <div class="mt-2 grid grid-cols-[auto_auto] gap-x-4 gap-y-1">
        <span class="text-gray-500">Sales</span>
        <span class="text-right font-medium">{format_currency(row.dollars)}</span>
        <span class="text-gray-500">Units</span>
        <span class="text-right font-medium">{format_number(row.units)}</span>
        <span class="text-gray-500">Base units</span>
        <span class="text-right font-medium">{format_number(row.base_units)}</span>
        <span class="text-gray-500">Incr units</span>
        <span class="text-right font-medium">{format_number(row.incremental_units)}</span>
      </div>
    </div>
  )
}

function BrandWeeklySkuTable(props: { rows: BrandWeeklyMovementSkuPoint[] }) {
  return (
    <Card>
      <CardHeader>
        <h3 class="text-base font-semibold text-gray-900">Top SKUs</h3>
      </CardHeader>
      <CardContent>
        <div class="overflow-x-auto">
          <table class="w-full text-sm">
            <thead>
              <tr class="border-b border-gray-200 text-left text-xs uppercase text-gray-500">
                <th class="py-2 pr-3">SKU</th>
                <th class="py-2 pr-3 text-right">Sales</th>
                <th class="py-2 pr-3 text-right">Units</th>
                <th class="py-2 pr-3 text-right">Base Units</th>
                <th class="py-2 pr-3 text-right">Incr Units</th>
                <th class="py-2 text-right">Incr Mix</th>
              </tr>
            </thead>
            <tbody>
              {props.rows.map((row: BrandWeeklyMovementSkuPoint) => (
                <tr key={`${row.upc || ""}-${row.description}`} class="border-b border-gray-100">
                  <td class="py-2 pr-3">
                    <div class="font-medium text-gray-900">{row.description}</div>
                    {row.upc && <div class="text-xs text-gray-500">{row.upc}</div>}
                  </td>
                  <td class="py-2 pr-3 text-right">{format_currency(row.dollars)}</td>
                  <td class="py-2 pr-3 text-right">{format_number(row.units)}</td>
                  <td class="py-2 pr-3 text-right">{format_number(row.base_units)}</td>
                  <td class="py-2 pr-3 text-right">{format_number(row.incremental_units)}</td>
                  <td class="py-2 text-right">{format_percent(row.incremental_units_pct)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </CardContent>
    </Card>
  )
}

function BrandWeeklyGeographyTable(props: { rows: BrandWeeklyMovementGeographyPoint[] }) {
  return (
    <Card>
      <CardHeader>
        <h3 class="text-base font-semibold text-gray-900">Top Geographies</h3>
      </CardHeader>
      <CardContent>
        <div class="overflow-x-auto">
          <table class="w-full text-sm">
            <thead>
              <tr class="border-b border-gray-200 text-left text-xs uppercase text-gray-500">
                <th class="py-2 pr-3">Geography</th>
                <th class="py-2 pr-3 text-right">Sales</th>
                <th class="py-2 pr-3 text-right">Units</th>
                <th class="py-2 pr-3 text-right">Base Units</th>
                <th class="py-2 text-right">Incr Units</th>
              </tr>
            </thead>
            <tbody>
              {props.rows.map((row: BrandWeeklyMovementGeographyPoint) => (
                <tr key={row.geography} class="border-b border-gray-100">
                  <td class="py-2 pr-3 font-medium text-gray-900">{row.geography}</td>
                  <td class="py-2 pr-3 text-right">{format_currency(row.dollars)}</td>
                  <td class="py-2 pr-3 text-right">{format_number(row.units)}</td>
                  <td class="py-2 pr-3 text-right">{format_number(row.base_units)}</td>
                  <td class="py-2 text-right">{format_number(row.incremental_units)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </CardContent>
    </Card>
  )
}

function BrandWeeklyMovementDashboard(props: {
  data: BrandWeeklyMovementResponse;
  report?: DistributorSalesReport;
  onGeographyChange: (geography: string) => void;
}) {
  return (
    <>
      <div class="rounded-lg border border-blue-200 bg-blue-50 p-4 text-sm text-blue-900">
        <p class="font-medium">Brand weekly movement report</p>
        <p class="mt-1">
          Showing weekly syndicated sales for {props.data.upload_brand_name || "the uploaded brand"} by geography and SKU. These reports do not include year-ago, ACV, TDP, or peer-brand fields, so the dashboard focuses on movement, base units, incremental units, and SKU contribution.
        </p>
      </div>

      <div class="rounded-lg border border-gray-200 bg-white p-5">
        <div class="flex flex-wrap items-end justify-between gap-4">
          <div>
            <p class="text-sm font-medium text-gray-900">
              {format_date_label(props.data.start_date)} - {format_date_label(props.data.end_date)}
            </p>
            <p class="mt-1 text-sm text-gray-500">
              {props.data.totals.week_count} weeks, {props.data.totals.geography_count} geographies, {props.data.totals.sku_count} SKUs
            </p>
          </div>
          <div class="w-72">
            <Label class="mb-2 block">Geography</Label>
            <Select value={props.data.selected_geography} onValueChange={props.onGeographyChange}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                {props.data.available_geographies.map((option: string) => (
                  <SelectItem key={option} value={option}>{option}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
      </div>

      <div class="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
        <KpiCard label="Sales" value={format_currency(props.data.totals.dollars)} detail={props.data.selected_geography} />
        <KpiCard label="Units" value={format_number(props.data.totals.units)} detail="Total movement" />
        <KpiCard label="Base Units" value={format_number(props.data.totals.base_units)} detail="Baseline movement" />
        <KpiCard label="Incremental Units" value={format_number(props.data.totals.incremental_units)} detail={`${format_percent(props.data.totals.incremental_units_pct)} of units`} />
        <KpiCard label="Average Price" value={props.data.totals.average_selling_price === null || props.data.totals.average_selling_price === undefined ? "-" : format_currency(props.data.totals.average_selling_price)} detail="Sales divided by units" />
        <KpiCard label="SKUs" value={`${props.data.totals.sku_count}`} detail="Unique UPCs/products" />
        <KpiCard label="Weeks" value={`${props.data.totals.week_count}`} detail="Weekly periods imported" />
        <KpiCard label="Geographies" value={`${props.data.totals.geography_count}`} detail="Markets available" />
      </div>

      <Card>
        <CardHeader>
          <h3 class="text-base font-semibold text-gray-900">Weekly Movement</h3>
        </CardHeader>
        <CardContent>
          <ChartContainer config={{ dollars: { label: "Sales $", color: "#2563eb" }, units: { label: "Units", color: "#16a34a" } }} className="h-80 w-full">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={props.data.trend} margin={{ left: 18, right: 18, top: 10, bottom: 48 }}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="end_date" tickFormatter={(value: string) => format(parseISO(value), "MMM d")} angle={-30} textAnchor="end" height={60} fontSize={11} />
                <YAxis yAxisId="left" tickFormatter={format_currency} width={74} label={{ value: "Sales $", angle: -90, position: "insideLeft" }} />
                <YAxis yAxisId="right" orientation="right" tickFormatter={format_number} width={70} label={{ value: "Units", angle: 90, position: "insideRight" }} />
                <Tooltip content={<BrandWeeklyMovementTooltip />} />
                <Bar yAxisId="left" dataKey="dollars" fill="#2563eb" radius={[4, 4, 0, 0]} />
                <Bar yAxisId="right" dataKey="units" fill="#16a34a" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </ChartContainer>
        </CardContent>
      </Card>

      <div class="grid gap-6 xl:grid-cols-[minmax(0,1.35fr)_minmax(360px,0.65fr)]">
        <BrandWeeklySkuTable rows={props.data.top_skus} />
        <BrandWeeklyGeographyTable rows={props.data.top_geographies} />
      </div>

      {props.report && (
        <p class="text-xs text-gray-500">
          Current report: {props.report.file_name}
        </p>
      )}
    </>
  )
}

function same_brand(left?: string | null, right?: string | null): boolean {
  return Boolean(left && right && left.toLowerCase() === right.toLowerCase())
}

function rows_with_my_brand(rows: SpinsBrandMetrics[], myBrand?: SpinsBrandMetrics | null): SpinsBrandMetrics[] {
  if (!myBrand) return rows
  if (rows.some((row: SpinsBrandMetrics) => same_brand(row.brand, myBrand.brand))) return rows
  return [...rows, myBrand]
}

export function is_syndicated_cache_key_for_report(key: unknown, reportId: string): boolean {
  if (key === "spins-reports") return true
  if (!Array.isArray(key)) return false
  const [namespace, keyReportId] = key
  return typeof namespace === "string" && namespace.startsWith("spins-") && keyReportId === reportId
}

if (import.meta.vitest) {
  const { describe, expect, it } = import.meta.vitest

  describe("is_syndicated_cache_key_for_report", () => {
    it("matches report list and selected report-specific syndicated keys", () => {
      expect(is_syndicated_cache_key_for_report("spins-reports", "report-1")).toBe(true)
      expect(is_syndicated_cache_key_for_report(["spins-overview", "report-1"], "report-1")).toBe(true)
      expect(is_syndicated_cache_key_for_report(["spins-products", "report-2"], "report-1")).toBe(false)
    })

    it("does not match non-syndicated keys", () => {
      expect(is_syndicated_cache_key_for_report(["distributor-sales", "report-1"], "report-1")).toBe(false)
      expect(is_syndicated_cache_key_for_report("other-key", "report-1")).toBe(false)
    })
  })
}

function AcvTooltip() {
  return (
    <span class="group relative inline-flex items-center">
      <HelpCircle className="h-3.5 w-3.5 text-gray-400" />
      <span class="pointer-events-none absolute right-0 top-5 z-10 hidden w-64 rounded-md border border-gray-200 bg-white p-3 text-left text-xs normal-case leading-5 text-gray-600 shadow-lg group-hover:block">
        ACV means all commodity volume. In syndicated data, average ACV % is a distribution measure: the weighted percent of the measured market where a product or brand is selling.
      </span>
    </span>
  )
}

function SppTooltip() {
  return (
    <span class="group relative inline-flex items-center">
      <HelpCircle className="h-3.5 w-3.5 text-gray-400" />
      <span class="pointer-events-none absolute right-0 top-5 z-10 hidden w-64 rounded-md border border-gray-200 bg-white p-3 text-left text-xs normal-case leading-5 text-gray-600 shadow-lg group-hover:block">
        SPP means sales per point of distribution. It helps compare velocity by showing dollars generated for each TDP point.
      </span>
    </span>
  )
}

function BrandMetricBarChart(props: {
  title: string;
  description: string;
  rows: SpinsBrandMetrics[];
  uploadBrandName?: string | null;
  metric: "avg_acv" | "spp";
}) {
  const chartRows = props.rows.map((row: SpinsBrandMetrics) => ({
    brand: row.brand,
    value: props.metric === "avg_acv" ? to_number(row.avg_acv) : row.dollars_per_tdp ?? 0,
    dollars: to_number(row.dollars),
    avg_acv: to_number(row.avg_acv),
    spp: row.dollars_per_tdp ?? null,
    tdp: to_number(row.tdp),
    is_my_brand: same_brand(row.brand, props.uploadBrandName),
  }))
  const isAcv = props.metric === "avg_acv"
  const valueLabel = isAcv ? "ACV %" : "SPP"
  const valueFormatter = (value: number) => isAcv ? format_percent(value) : format_currency(value)

  return (
    <Card>
      <CardHeader>
        <div>
          <h3 class="text-base font-semibold text-gray-900">
            <span class="inline-flex items-center gap-1">
              {props.title}
              {isAcv ? <AcvTooltip /> : <SppTooltip />}
            </span>
          </h3>
          <p class="mt-1 text-sm text-gray-500">{props.description}</p>
        </div>
      </CardHeader>
      <CardContent>
        <ChartContainer config={{ value: { label: valueLabel, color: "#2563eb" } }} className="h-72 w-full">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={chartRows} margin={{ left: 18, right: 18, top: 10, bottom: 58 }}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="brand" angle={-35} textAnchor="end" height={70} interval={0} fontSize={11} />
              <YAxis tickFormatter={valueFormatter} width={74} label={{ value: valueLabel, angle: -90, position: "insideLeft" }} />
              <Tooltip content={<BrandMetricTooltip valueFormatter={valueFormatter} valueLabel={valueLabel} />} />
              <Bar dataKey="value" name={valueLabel} radius={[4, 4, 0, 0]}>
                {chartRows.map((row: BrandMetricChartRow) => (
                  <Cell key={`${props.metric}-${row.brand}`} fill={row.is_my_brand ? "#f97316" : "#2563eb"} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </ChartContainer>
        <ScatterLegend uploadBrandName={props.uploadBrandName} />
      </CardContent>
    </Card>
  )
}

function BrandTable(props: {
  title: string;
  rows: SpinsBrandMetrics[];
  myBrand?: SpinsBrandMetrics | null;
  activeBrand?: string;
  showDollarGain?: boolean;
  onSelectBrand: (brand: string) => void;
}) {
  const displayRows = rows_with_my_brand(props.rows, props.myBrand)

  return (
    <Card>
      <CardHeader>
        <h3 class="text-base font-semibold text-gray-900">{props.title}</h3>
      </CardHeader>
      <CardContent>
        <div class="overflow-x-auto">
          <table class="w-full text-sm">
            <thead>
              <tr class="border-b border-gray-200 text-left text-xs uppercase text-gray-500">
                <th class="py-2 pr-3">Brand</th>
                <th class="py-2 pr-3 text-right">Sales</th>
                {props.showDollarGain && <th class="py-2 pr-3 text-right">$ Gain</th>}
                <th class="py-2 pr-3 text-right">YoY</th>
                <th class="py-2 pr-3 text-right">Share</th>
                <th class="py-2 pr-3 text-right">TDP</th>
                <th class="py-2 text-right">Promo</th>
              </tr>
            </thead>
            <tbody>
              {displayRows.map((row: SpinsBrandMetrics) => {
                const isMyBrand = same_brand(row.brand, props.myBrand?.brand)
                return (
                <tr
                  key={row.brand}
                  class={`border-b border-gray-100 ${row.brand === props.activeBrand ? "bg-blue-50" : isMyBrand ? "bg-orange-50" : "hover:bg-gray-50"}`}
                >
                  <td class="py-2 pr-3">
                    <button class="font-medium text-gray-900 hover:text-blue-700" onClick={() => props.onSelectBrand(row.brand)}>
                      {row.rank ? `#${row.rank} ` : ""}{row.brand}
                      {isMyBrand && <span class="ml-2 rounded-full bg-orange-100 px-2 py-0.5 text-xs font-semibold text-orange-700">My Brand</span>}
                    </button>
                  </td>
                  <td class="py-2 pr-3 text-right">{format_currency(row.dollars)}</td>
                  {props.showDollarGain && (
                    <td class={`py-2 pr-3 text-right ${metric_color(to_number(row.dollars_change))}`}>
                      {format_currency(row.dollars_change)}
                    </td>
                  )}
                  <td class={`py-2 pr-3 text-right ${metric_color(row.dollars_pct_change)}`}>
                    {format_percent(row.dollars_pct_change)}
                  </td>
                  <td class="py-2 pr-3 text-right">{format_percent(row.share_pct)}</td>
                  <td class="py-2 pr-3 text-right">{format_number(row.tdp)}</td>
                  <td class="py-2 text-right">{format_percent(row.promo_mix_pct)}</td>
                </tr>
              )})}
            </tbody>
          </table>
        </div>
      </CardContent>
    </Card>
  )
}

function ProductTable(props: {
  products: SpinsProductMetrics[];
  brand: string;
  brandOptions: string[];
  uploadBrandName?: string | null;
  onBrandChange: (brand: string) => void;
}) {
  return (
    <Card>
      <CardHeader>
        <div class="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h3 class="text-base font-semibold text-gray-900">SKU Contribution</h3>
            <p class="mt-1 text-sm text-gray-500">
              Showing {props.brand || props.uploadBrandName || "selected brand"} products.
            </p>
          </div>
          <div class="w-64">
            <Label class="mb-2 block">SKU Brand</Label>
            <Select value={props.brand} onValueChange={props.onBrandChange}>
              <SelectTrigger>
                <SelectValue placeholder="Select brand..." />
              </SelectTrigger>
              <SelectContent>
                {props.brandOptions.map((brand_name: string) => (
                  <SelectItem key={brand_name} value={brand_name}>
                    {brand_name === props.uploadBrandName ? `${brand_name} (My Brand)` : brand_name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <div class="overflow-x-auto">
          <table class="w-full text-sm">
            <thead>
              <tr class="border-b border-gray-200 text-left text-xs uppercase text-gray-500">
                <th class="py-2 pr-3">Product</th>
                <th class="py-2 pr-3 text-right">Sales</th>
                <th class="py-2 pr-3 text-right">Change</th>
                <th class="py-2 pr-3 text-right">YoY</th>
                <th class="py-2 pr-3 text-right">
                  <span class="inline-flex items-center justify-end gap-1">
                    ACV %
                    <AcvTooltip />
                  </span>
                </th>
                <th class="py-2 pr-3 text-right">
                  <span class="inline-flex items-center justify-end gap-1">
                    SPP
                    <SppTooltip />
                  </span>
                </th>
                <th class="py-2 text-right">Promo</th>
              </tr>
            </thead>
            <tbody>
              {props.products.slice(0, 20).map((product: SpinsProductMetrics) => (
                <tr key={`${product.description}-${product.upc || ""}`} class="border-b border-gray-100">
                  <td class="py-2 pr-3">
                    <div class="font-medium text-gray-900">{product.description}</div>
                    {product.upc && <div class="text-xs text-gray-500">{product.upc}</div>}
                  </td>
                  <td class="py-2 pr-3 text-right">{format_currency(product.dollars)}</td>
                  <td class={`py-2 pr-3 text-right ${metric_color(to_number(product.dollars_change))}`}>
                    {format_currency(product.dollars_change)}
                  </td>
                  <td class={`py-2 pr-3 text-right ${metric_color(product.dollars_pct_change)}`}>
                    {format_percent(product.dollars_pct_change)}
                  </td>
                  <td class="py-2 pr-3 text-right">{format_percent_value(product.avg_acv)}</td>
                  <td class="py-2 pr-3 text-right">{format_sku_spp(product)}</td>
                  <td class="py-2 text-right">{format_percent(product.promo_mix_pct)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </CardContent>
    </Card>
  )
}

function SkuComparisonTable(props: {
  products: SpinsProductMetrics[];
  myBrandName?: string | null;
  selectedBrand?: string;
}) {
  const rows = useMemo(() => {
    const fastestRows = [...props.products]
      .filter((product: SpinsProductMetrics) => product.dollars_pct_change !== null && product.dollars_pct_change !== undefined)
      .sort((left: SpinsProductMetrics, right: SpinsProductMetrics) => (right.dollars_pct_change ?? -Infinity) - (left.dollars_pct_change ?? -Infinity))
      .slice(0, 20)

    const myBrandRows = props.products
      .filter((product: SpinsProductMetrics) => same_brand(product.brand, props.myBrandName))
      .sort((left: SpinsProductMetrics, right: SpinsProductMetrics) => to_number(right.dollars) - to_number(left.dollars))
      .slice(0, 8)

    const selectedBrandRows = props.products
      .filter((product: SpinsProductMetrics) => same_brand(product.brand, props.selectedBrand))
      .sort((left: SpinsProductMetrics, right: SpinsProductMetrics) => to_number(right.dollars) - to_number(left.dollars))
      .slice(0, 8)

    const byKey = new Map<string, SpinsProductMetrics>()
    for (const product of [...myBrandRows, ...selectedBrandRows, ...fastestRows]) {
      byKey.set(`${product.brand}-${product.upc || product.description}`, product)
    }
    return Array.from(byKey.values())
  }, [props.products, props.myBrandName, props.selectedBrand])

  return (
    <Card>
      <CardHeader>
        <div>
          <h3 class="text-base font-semibold text-gray-900">SKU Peer Comparison</h3>
          <p class="mt-1 text-sm text-gray-500">
            My Brand SKUs plus the fastest-growing category SKUs in this report.
          </p>
        </div>
      </CardHeader>
      <CardContent>
        <div class="overflow-x-auto">
          <table class="w-full text-sm">
            <thead>
              <tr class="border-b border-gray-200 text-left text-xs uppercase text-gray-500">
                <th class="py-2 pr-3">SKU</th>
                <th class="py-2 pr-3">Brand</th>
                <th class="py-2 pr-3 text-right">Sales</th>
                <th class="py-2 pr-3 text-right">$ Gain</th>
                <th class="py-2 pr-3 text-right">YoY</th>
                <th class="py-2 pr-3 text-right">
                  <span class="inline-flex items-center justify-end gap-1">
                    ACV %
                    <AcvTooltip />
                  </span>
                </th>
                <th class="py-2 pr-3 text-right">
                  <span class="inline-flex items-center justify-end gap-1">
                    SPP
                    <SppTooltip />
                  </span>
                </th>
                <th class="py-2 text-right">Promo</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((product: SpinsProductMetrics) => {
                const isMyBrand = same_brand(product.brand, props.myBrandName)
                return (
                  <tr
                    key={`${product.brand}-${product.description}-${product.upc || ""}`}
                    class={`border-b border-gray-100 ${isMyBrand ? "bg-orange-50" : "hover:bg-gray-50"}`}
                  >
                    <td class="py-2 pr-3">
                      <div class="font-medium text-gray-900">{product.description}</div>
                      {product.upc && <div class="text-xs text-gray-500">{product.upc}</div>}
                    </td>
                    <td class="py-2 pr-3">
                      <span class="font-medium text-gray-800">{product.brand}</span>
                      {isMyBrand && <span class="ml-2 rounded-full bg-orange-100 px-2 py-0.5 text-xs font-semibold text-orange-700">My Brand</span>}
                    </td>
                    <td class="py-2 pr-3 text-right">{format_currency(product.dollars)}</td>
                    <td class={`py-2 pr-3 text-right ${metric_color(to_number(product.dollars_change))}`}>
                      {format_currency(product.dollars_change)}
                    </td>
                    <td class={`py-2 pr-3 text-right ${metric_color(product.dollars_pct_change)}`}>
                      {format_percent(product.dollars_pct_change)}
                    </td>
                    <td class="py-2 pr-3 text-right">{format_percent_value(product.avg_acv)}</td>
                    <td class="py-2 pr-3 text-right">{format_sku_spp(product)}</td>
                    <td class="py-2 text-right">{format_percent(product.promo_mix_pct)}</td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      </CardContent>
    </Card>
  )
}

function SelectedBrandControl(props: {
  brands: string[];
  search: string;
  results: string[];
  isOpen: boolean;
  uploadBrandName?: string | null;
  onSearchChange: (value: string) => void;
  onOpenChange: (isOpen: boolean) => void;
  onAdd: (brand: string) => void;
  onRemove: (brand: string) => void;
  onFocus: (brand: string) => void;
}) {
  const availableResults = props.results
    .filter((brand_name: string) => !props.brands.includes(brand_name))

  return (
    <Card>
      <CardHeader>
        <div class="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h3 class="text-base font-semibold text-gray-900">Comparison Brands</h3>
            <p class="mt-1 text-sm text-gray-500">
              Selected brands drive every syndicated-data comparison chart.
            </p>
          </div>
          <div class="relative w-full max-w-md">
            <Search className="pointer-events-none absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
            <Input
              className="pl-9"
              value={props.search}
              placeholder="Search brands to add..."
              onFocus={() => props.onOpenChange(true)}
              onKeyDown={(e: KeyboardEvent) => {
                if (e.key === "Escape") props.onOpenChange(false)
              }}
              onInput={(e: Event) => props.onSearchChange((e.target as HTMLInputElement).value)}
            />
            {props.isOpen && (
              <div
                class="absolute z-20 mt-1 max-h-80 w-full overflow-y-auto rounded-md border border-gray-200 bg-white py-1 shadow-lg"
                onMouseDown={(e: MouseEvent) => e.preventDefault()}
              >
                <div class="border-b border-gray-100 px-3 py-2 text-xs font-medium uppercase tracking-wide text-gray-500">
                  {availableResults.length} available brand{availableResults.length === 1 ? "" : "s"}
                </div>
                {availableResults.map((brand_name: string) => (
                  <button
                    key={brand_name}
                    class="block w-full px-3 py-2 text-left text-sm text-gray-800 hover:bg-blue-50 hover:text-blue-700"
                    onClick={() => props.onAdd(brand_name)}
                  >
                    {brand_name}
                  </button>
                ))}
                {availableResults.length === 0 && (
                  <div class="px-3 py-3 text-sm text-gray-500">No matching brands available.</div>
                )}
              </div>
            )}
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <div class="flex flex-wrap gap-2">
          {props.brands.map((brand_name: string) => {
            const isUploadBrand = props.uploadBrandName === brand_name
            return (
              <span
                key={brand_name}
                class={`inline-flex items-center gap-2 rounded-full border px-3 py-1 text-sm ${
                  isUploadBrand ? "border-orange-300 bg-orange-50 text-orange-800" : "border-gray-200 bg-white text-gray-700"
                }`}
              >
                <button class="font-medium hover:underline" onClick={() => props.onFocus(brand_name)}>
                  {brand_name}
                </button>
                {!isUploadBrand && (
                  <button class="text-gray-400 hover:text-gray-700" onClick={() => props.onRemove(brand_name)} aria-label={`Remove ${brand_name}`}>
                    x
                  </button>
                )}
              </span>
            )
          })}
        </div>
      </CardContent>
    </Card>
  )
}

interface SpinsSalesSectionProps {
  className?: string
}

export function SpinsSalesSection({ className }: SpinsSalesSectionProps) {
  const { mutate } = useSWRConfig()
  const [reportId, setReportId] = useState("")
  const [weeks, setWeeks] = useState<number | undefined>(undefined)
  const [geography, setGeography] = useState<string | undefined>(undefined)
  const [brand, setBrand] = useState<string | undefined>(undefined)
  const [skuBrand, setSkuBrand] = useState<string>("")
  const [brandSearch, setBrandSearch] = useState("")
  const [brandSearchOpen, setBrandSearchOpen] = useState(false)
  const [comparisonBrands, setComparisonBrands] = useState<string[]>([])

  const { data: reports = [], mutate: reloadReports, isLoading: reportsLoading } = useSWR(
    "spins-reports",
    async () => (await listDistributorReports({ source: "spins" })).filter((report: DistributorSalesReport) => report.source === "spins"),
    { revalidateOnFocus: false }
  )

  useEffect(() => {
    if (!reportId && reports.length > 0) {
      setReportId(reports[0].id)
    }
  }, [reportId, reports])

  const selectedReport = reports.find((report: DistributorSalesReport) => report.id === reportId)
  const isBrandWeeklyMovementReport = selectedReport?.report_type === "brand_weekly_movement"

  const { data: brandsData } = useSWR(
    reportId && !isBrandWeeklyMovementReport ? ["spins-brands", reportId, weeks, geography, brandSearch] : null,
    () => getSpinsBrands({ report_id: reportId, weeks, geography, query: brandSearch }),
    { revalidateOnFocus: false }
  )

  const { data: allBrandsData } = useSWR(
    reportId && !isBrandWeeklyMovementReport ? ["spins-all-brands", reportId, weeks, geography] : null,
    () => getSpinsBrands({ report_id: reportId, weeks, geography }),
    { revalidateOnFocus: false }
  )

  const { data: overview, isLoading: overviewLoading } = useSWR(
    reportId && !isBrandWeeklyMovementReport ? ["spins-overview", reportId, weeks, geography, brand] : null,
    () => getSpinsOverview({ report_id: reportId, weeks, geography, brand }),
    { revalidateOnFocus: false }
  )

  const { data: brandMovement, isLoading: brandMovementLoading } = useSWR(
    reportId && isBrandWeeklyMovementReport ? ["spins-brand-weekly-movement", reportId, geography] : null,
    () => getBrandWeeklyMovement({ report_id: reportId, geography }),
    { revalidateOnFocus: false }
  )

  useEffect(() => {
    if (!overview) return
    if (!brand && overview.upload_brand_name) {
      setBrand(overview.upload_brand_name)
    }
    if (!skuBrand && overview.upload_brand_name) {
      setSkuBrand(overview.upload_brand_name)
    }
    if (!weeks) {
      setWeeks(overview.selected_weeks)
    }
    if (!geography) {
      setGeography(overview.selected_geography)
    }
    if (comparisonBrands.length === 0) {
      const defaults = [
        overview.upload_brand_name,
        ...overview.peer_brands.map((row: SpinsBrandMetrics) => row.brand),
      ].filter((value: string | null | undefined): value is string => Boolean(value))
      setComparisonBrands(Array.from(new Set(defaults)))
    }
  }, [overview, brand, skuBrand, weeks, geography, comparisonBrands.length])

  useEffect(() => {
    if (!brandMovement) return
    if (!geography) {
      setGeography(brandMovement.selected_geography)
    }
  }, [brandMovement, geography])

  const { data: comparison } = useSWR(
    reportId && !isBrandWeeklyMovementReport && comparisonBrands.length > 0 ? ["spins-comparison", reportId, weeks, geography, comparisonBrands.join("|")] : null,
    () => getSpinsBrandComparison({ report_id: reportId, weeks, geography, brands: comparisonBrands }),
    { revalidateOnFocus: false }
  )

  const { data: products } = useSWR(
    reportId && !isBrandWeeklyMovementReport && (skuBrand || overview?.upload_brand_name) ? ["spins-products", reportId, weeks, geography, skuBrand || overview?.upload_brand_name] : null,
    () => getSpinsProducts({ report_id: reportId, weeks, geography, brand: skuBrand || overview?.upload_brand_name || undefined }),
    { revalidateOnFocus: false }
  )

  const { data: categoryProducts } = useSWR(
    reportId && !isBrandWeeklyMovementReport ? ["spins-category-products", reportId, weeks, geography] : null,
    () => getSpinsProducts({ report_id: reportId, weeks, geography }),
    { revalidateOnFocus: false }
  )

  const scatterData = useMemo(() => {
    return (comparison?.brands ?? []).map((row: SpinsBrandMetrics) => ({
      brand: row.brand,
      growth: row.dollars_pct_change ?? 0,
      tdp_change: to_number(row.tdp_change),
      dollars: to_number(row.dollars),
      is_upload_brand: row.brand === overview?.upload_brand_name,
    }))
  }, [comparison?.brands, overview?.upload_brand_name])
  const comparisonRows = comparison?.brands ?? []
  const competitorScatterData = scatterData.filter((row: any) => !row.is_upload_brand)
  const uploadBrandScatterData = scatterData.filter((row: any) => row.is_upload_brand)
  const myBrandRow = comparisonRows.find((row: SpinsBrandMetrics) => same_brand(row.brand, overview?.upload_brand_name))
    || (same_brand(overview?.brand.brand, overview?.upload_brand_name) ? overview?.brand : null)

  function choose_brand(nextBrand: string) {
    setBrand(nextBrand)
    setComparisonBrands((prev: string[]) => Array.from(new Set([nextBrand, ...prev])).slice(0, 12))
  }

  function add_comparison_brand(nextBrand: string) {
    setComparisonBrands((prev: string[]) => Array.from(new Set([...prev, nextBrand])).slice(0, 12))
    setBrandSearch("")
    setBrandSearchOpen(false)
  }

  function remove_comparison_brand(nextBrand: string) {
    if (nextBrand === overview?.upload_brand_name) return
    setComparisonBrands((prev: string[]) => prev.filter((brand_name: string) => brand_name !== nextBrand))
  }

  async function handle_syndicated_upload_complete(response: UploadResponse) {
    setReportId(response.report_id)
    setWeeks(undefined)
    setGeography(undefined)
    setBrand(undefined)
    setSkuBrand("")
    setBrandSearch("")
    setBrandSearchOpen(false)
    setComparisonBrands([])
    await reloadReports()
    await mutate(
      (key: unknown) => is_syndicated_cache_key_for_report(key, response.report_id),
      undefined,
      { revalidate: true }
    )
  }

  if (reportsLoading) {
    return (
      <div class={`flex items-center justify-center rounded-lg border border-gray-200 bg-white py-12 ${className || ""}`}>
        <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
        <span class="ml-3 text-gray-500">Loading syndicated data reports...</span>
      </div>
    )
  }

  return (
    <div class={`space-y-6 ${className || ""}`}>
      {reportId && !isBrandWeeklyMovementReport && (
        <SalesWizPanel
          reportId={reportId}
          weeks={weeks || overview?.selected_weeks}
          geography={geography || overview?.selected_geography}
          brand={brand || overview?.upload_brand_name || overview?.selected_brand}
        />
      )}

      <div class="rounded-lg border border-gray-200 bg-white p-5">
        <div class="flex flex-wrap items-end gap-4">
          <div class="w-72">
            <Label class="mb-2 block">Syndicated Data Report</Label>
            <Select value={reportId} onValueChange={(value: string) => {
              setReportId(value)
              setWeeks(undefined)
              setGeography(undefined)
              setBrand(undefined)
              setSkuBrand("")
              setBrandSearch("")
              setBrandSearchOpen(false)
              setComparisonBrands([])
            }}>
              <SelectTrigger>
                <SelectValue placeholder="Select syndicated data report..." />
              </SelectTrigger>
              <SelectContent>
                {reports.map((report: DistributorSalesReport) => (
                  <SelectItem key={report.id} value={report.id}>
                    {format(parseISO(report.report_date), "MMM d, yyyy")} - {report.file_name}
                    {report.report_type === "brand_weekly_movement" ? " (Weekly Movement)" : ""}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          {!isBrandWeeklyMovementReport && <SyndicatedMetricsImport report_id={reportId} />}
          {overview && (
            <>
              <div class="w-40">
                <Label class="mb-2 block">Time Frame</Label>
                <Select value={`${overview.selected_weeks}`} onValueChange={(value: string) => setWeeks(Number(value))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {overview.available_weeks.map((option: number) => (
                      <SelectItem key={option} value={`${option}`}>{option} weeks</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div class="w-72">
                <Label class="mb-2 block">Geography</Label>
                <Select value={overview.selected_geography} onValueChange={(value: string) => setGeography(value)}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {overview.available_geographies.map((option: string) => (
                      <SelectItem key={option} value={option}>{option}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <Button variant="outline" onClick={() => overview.upload_brand_name && choose_brand(overview.upload_brand_name)}>
                My Brand
              </Button>
            </>
          )}
          <Button variant="ghost" onClick={() => reloadReports()}>
            <RefreshCw className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {reports.length === 0 ? (
        <div class="rounded-lg border border-gray-200 bg-white p-6">
          <p class="mb-4 text-sm text-gray-600">No syndicated data reports have been uploaded yet.</p>
          <DistributorSalesUpload onUploadComplete={handle_syndicated_upload_complete} className="max-w-xl" />
        </div>
      ) : isBrandWeeklyMovementReport ? (
        brandMovementLoading || !brandMovement ? (
          <div class="flex items-center justify-center rounded-lg border border-gray-200 bg-white py-12">
            <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
            <span class="ml-3 text-gray-500">Loading weekly movement analysis...</span>
          </div>
        ) : (
          <BrandWeeklyMovementDashboard
            data={brandMovement}
            report={selectedReport}
            onGeographyChange={setGeography}
          />
        )
      ) : overviewLoading || !overview ? (
        <div class="flex items-center justify-center rounded-lg border border-gray-200 bg-white py-12">
          <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
          <span class="ml-3 text-gray-500">Loading syndicated data analysis...</span>
        </div>
      ) : (
        <>
          <div class="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
            <KpiCard label="Brand Sales" value={format_currency(overview.brand.dollars)} detail={`${format_currency(overview.brand.dollars_change)} vs YAGO`} />
            <KpiCard label="Dollar Growth" value={format_percent(overview.brand.dollars_pct_change)} tone={metric_color(overview.brand.dollars_pct_change)} detail="Year ago comparison" />
            <KpiCard label="Rank / Share" value={`#${overview.brand.rank || "-"}`} detail={`${format_percent(overview.brand.share_pct)} category share`} />
            <KpiCard label="TDP" value={format_number(overview.brand.tdp)} detail={`${format_number(overview.brand.tdp_change)} vs YAGO`} />
            <KpiCard label="ACV %" value={format_percent_value(overview.brand.avg_acv)} detail={`${to_number(overview.brand.avg_acv_change).toFixed(1)} pts vs YAGO`} />
            <KpiCard label="SPP" value={overview.brand.dollars_per_tdp === null || overview.brand.dollars_per_tdp === undefined ? "-" : format_currency(overview.brand.dollars_per_tdp)} detail="Sales per point of distribution" />
            <KpiCard label="Promo Mix" value={format_percent(overview.brand.promo_mix_pct)} detail={format_currency(overview.brand.promo_dollars)} />
            <KpiCard label="Incremental Mix" value={format_percent(overview.brand.incremental_mix_pct)} detail={format_currency(overview.brand.incremental_dollars)} />
          </div>

          <SelectedBrandControl
            brands={comparisonBrands}
            search={brandSearch}
            results={brandsData?.brands ?? []}
            isOpen={brandSearchOpen}
            uploadBrandName={overview.upload_brand_name}
            onSearchChange={setBrandSearch}
            onOpenChange={setBrandSearchOpen}
            onAdd={add_comparison_brand}
            onRemove={remove_comparison_brand}
            onFocus={choose_brand}
          />

          <div class="grid gap-6 xl:grid-cols-[minmax(0,1.35fr)_minmax(360px,0.65fr)]">
            <Card>
              <CardHeader>
                <h3 class="text-base font-semibold text-gray-900">Brand Comparison</h3>
              </CardHeader>
              <CardContent>
                <ChartContainer config={{ dollars: { label: "Sales $", color: "#2563eb" }, growth: { label: "Dollar Growth YoY %", color: "#16a34a" } }} className="h-80 w-full">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={comparisonRows} margin={{ left: 18, right: 18, top: 10, bottom: 58 }}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="brand" angle={-35} textAnchor="end" height={70} interval={0} fontSize={11} />
                      <YAxis yAxisId="left" tickFormatter={format_currency} width={74} label={{ value: "Sales $", angle: -90, position: "insideLeft" }} />
                      <YAxis yAxisId="right" orientation="right" tickFormatter={(value: number) => `${value}%`} width={58} label={{ value: "YoY $ Growth", angle: 90, position: "insideRight" }} />
                      <ReferenceLine yAxisId="right" y={0} stroke="#94a3b8" />
                      <Tooltip content={<BrandComparisonTooltip />} />
                      <Bar yAxisId="left" dataKey="dollars" name="Sales $" radius={[4, 4, 0, 0]}>
                        {comparisonRows.map((row: SpinsBrandMetrics) => (
                          <Cell key={`sales-${row.brand}`} fill={sales_bar_fill(row, overview.upload_brand_name)} />
                        ))}
                      </Bar>
                      <Bar yAxisId="right" dataKey="dollars_pct_change" name="Dollar Growth YoY %" radius={[4, 4, 0, 0]}>
                        {comparisonRows.map((row: SpinsBrandMetrics) => (
                          <Cell key={`growth-${row.brand}`} fill={growth_bar_fill(row, overview.upload_brand_name)} />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </ChartContainer>
                <ChartLegend />
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <h3 class="text-base font-semibold text-gray-900">Growth vs Distribution</h3>
              </CardHeader>
              <CardContent>
                <ChartContainer config={{ brands: { label: "Brands", color: "#2563eb" } }} className="h-80 w-full">
                  <ResponsiveContainer width="100%" height="100%">
                    <ScatterChart margin={{ left: 22, right: 24, top: 18, bottom: 30 }}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="tdp_change" name="TDP change" tickFormatter={(value: number) => value.toFixed(0)} label={{ value: "TDP Change vs YAGO", position: "insideBottom", offset: -14 }} />
                      <YAxis dataKey="growth" name="Dollar growth" tickFormatter={(value: number) => `${value.toFixed(0)}%`} label={{ value: "Dollar Growth YoY %", angle: -90, position: "insideLeft" }} />
                      <ReferenceLine x={0} stroke="#94a3b8" />
                      <ReferenceLine y={0} stroke="#94a3b8" />
                      <Tooltip cursor={{ strokeDasharray: "3 3" }} content={<GrowthDistributionTooltip />} />
                      <Scatter name="Competitors" data={competitorScatterData} fill="#2563eb">
                        <LabelList dataKey="brand" position="top" fontSize={10} fill="#475569" />
                      </Scatter>
                      <Scatter name={overview.upload_brand_name || "My Brand"} data={uploadBrandScatterData} fill="#f97316">
                        <LabelList dataKey="brand" position="top" fontSize={11} fill="#9a3412" fontWeight={700} />
                      </Scatter>
                    </ScatterChart>
                  </ResponsiveContainer>
                </ChartContainer>
                <ScatterLegend uploadBrandName={overview.upload_brand_name} />
              </CardContent>
            </Card>
          </div>

          <div class="grid gap-6 xl:grid-cols-2">
            <BrandMetricBarChart
              title="ACV % by Brand"
              description="Distribution reach across the selected comparison brands."
              rows={comparisonRows}
              uploadBrandName={overview.upload_brand_name}
              metric="avg_acv"
            />
            <BrandMetricBarChart
              title="SPP by Brand"
              description="Sales productivity per point of distribution."
              rows={comparisonRows}
              uploadBrandName={overview.upload_brand_name}
              metric="spp"
            />
          </div>

          <div class="grid gap-6 xl:grid-cols-3">
            <BrandTable
              title="Top Brands"
              rows={overview.top_brands}
              myBrand={myBrandRow}
              activeBrand={overview.selected_brand}
              onSelectBrand={choose_brand}
            />
            <BrandTable
              title="Top Dollar Gainers"
              rows={overview.top_gainers}
              myBrand={myBrandRow}
              activeBrand={overview.selected_brand}
              showDollarGain
              onSelectBrand={choose_brand}
            />
            <BrandTable
              title="Fastest Growers"
              rows={overview.fastest_growers}
              myBrand={myBrandRow}
              activeBrand={overview.selected_brand}
              onSelectBrand={choose_brand}
            />
          </div>

          <ProductTable
            products={products?.products ?? []}
            brand={skuBrand || overview.upload_brand_name || overview.selected_brand}
            brandOptions={allBrandsData?.brands ?? comparisonBrands}
            uploadBrandName={overview.upload_brand_name}
            onBrandChange={setSkuBrand}
          />

          <SkuComparisonTable
            products={categoryProducts?.products ?? []}
            myBrandName={overview.upload_brand_name}
            selectedBrand={skuBrand || overview.selected_brand}
          />

          <details class="rounded-lg border border-gray-200 bg-white">
            <summary class="cursor-pointer px-4 py-3 font-medium hover:bg-gray-50">Upload New Syndicated Data Report</summary>
            <div class="border-t border-gray-200 p-4">
              <DistributorSalesUpload onUploadComplete={handle_syndicated_upload_complete} className="max-w-xl" />
            </div>
          </details>

          {selectedReport && (
            <p class="text-xs text-gray-500">
              Current report: {selectedReport.file_name}
            </p>
          )}
        </>
      )}
    </div>
  )
}
